import { MutableRefObject, useEffect } from 'react'
import get from 'lodash/get'
import isArray from 'lodash/isArray'
import moment from 'moment'

import { getObjectCodeUrl } from '@pages/ConfiguredEntity/helpers'

import { getDynamicColumnFieldKey } from '@components/TimeLineGrid/helpers/getDynamicColumnFieldKey'

import { useExecuteCommandMutation } from '@redux/api'

import { useSubscription } from '@hooks'
import { getCommandRequestData, getRSqlBySearch, isObjectValueType } from '@helpers'
import { EVENT_CODE, EVENT_NAME_OBJECT_MESSAGE, TIME_LINE_MODE } from '@constants'
import {
  DateIntervalKey,
  MessageBody,
  ObjectDataRecord,
  ObjectFieldDTO,
  parseMessageBody,
  POSTObjectDataFilter,
  TimelineCellMove,
  TimeLineDTOType,
  TimeLineGridDataRecord,
} from '@types'

import {
  getDateIntervalColumnFieldKey,
  getExpandsParamForObjectX,
  getKeyFieldY,
  getObjectXLinkToObjectYKey,
  getObjectXSAExtraRsqlFilters,
} from '../helpers'

type UseObjectXSubscribeWS = {
  config: TimeLineDTOType
  timeLineGridData: TimeLineGridDataRecord[]
  handleChangeTimeLineGridData: (data: TimeLineGridDataRecord[], isRefresh?: boolean) => void
  objectXPKField: ObjectFieldDTO | undefined
  getColumnTotals: (data: { rowIndex: number; key: string; srcObj: ObjectDataRecord }) => void
  handleExecuteActionWithSetVariables: (data: { eventCode: EVENT_CODE }) => void
  handleDeleteTimeLineObjectXEventById: (objectId: string) => void
  handleAddTimeLineObjectXEvent: (data: {
    itemIndex: number
    key: string
    newItem: ObjectDataRecord
    objectId: string
  }) => void
  handleUpdateTimeLineObjectXEvent: (data: {
    itemIndex: number
    key: string
    newItem: ObjectDataRecord
    objectId: string
  }) => void
  objectXSARSQLFilter: string | null
  objectYSAFiltersCriteria?: POSTObjectDataFilter
  objectXColumnsObjectSAFiltersCriteria?: POSTObjectDataFilter
  columnsObjectData: ObjectDataRecord[]
  dragItemsForGetTotals: MutableRefObject<TimelineCellMove[]>
  handleDeleteDragItemForGetTotals: (sourceColumnId: string) => void
}

export const useObjectXSubscribeWS = (props: UseObjectXSubscribeWS) => {
  const {
    config,
    timeLineGridData,
    getColumnTotals,
    objectXPKField,
    handleExecuteActionWithSetVariables,
    handleDeleteTimeLineObjectXEventById,
    handleAddTimeLineObjectXEvent,
    handleUpdateTimeLineObjectXEvent,
    objectXSARSQLFilter,
    objectYSAFiltersCriteria,
    objectXColumnsObjectSAFiltersCriteria,
    columnsObjectData,
    dragItemsForGetTotals,
    handleDeleteDragItemForGetTotals,
  } = props

  const [executeCommand] = useExecuteCommandMutation()

  const getIndicesForChanging = (changedItem: ObjectDataRecord) => {
    const itemIndex = timeLineGridData.findIndex(
      item =>
        item.yElement[getKeyFieldY(config)] ===
        get(changedItem, getObjectXLinkToObjectYKey(config.objectX.linkToObjectY))
    )

    let key = ''
    let columnInfo: ObjectDataRecord = {}

    if (config.mode === TIME_LINE_MODE.DATE_TIME && config.objectX.startDateField) {
      if (itemIndex === -1 || !changedItem[config.objectX.startDateField]) {
        return {
          dateIntervalKey: undefined,
          itemIndex: undefined,
          columnInfo,
        }
      }

      const startDate = moment(changedItem[config.objectX.startDateField])
        .startOf('day')
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')

      const endDate = moment(changedItem[config.objectX.startDateField])
        .endOf('day')
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')

      key = getDateIntervalColumnFieldKey(startDate, endDate) as keyof DateIntervalKey
      columnInfo = {
        startDate,
        endDate,
      }
    }

    if (config.mode === TIME_LINE_MODE.OBJECT && config.objectX.linkToColumnsObject) {
      if (itemIndex === -1) {
        return {
          dateIntervalKey: undefined,
          itemIndex: undefined,
          columnInfo,
        }
      }

      key = getDynamicColumnFieldKey(changedItem, config.objectX.linkToColumnsObject)
      columnInfo =
        columnsObjectData.find(
          item =>
            getDynamicColumnFieldKey(item, config.objectX.fieldFromColumnsObjectToLink) === key
        ) || {}
    }

    return {
      key,
      itemIndex,
      columnInfo,
    }
  }

  const getObjectXParams = (body: MessageBody) => {
    const objectXCommand = config.objectX.getDataCommand
    const objectXParams = objectXCommand?.parameters
      ? getCommandRequestData(objectXCommand.parameters, {})
      : {}

    const objectXParamsExpands =
      typeof objectXParams['expands'] === 'string' ? objectXParams['expands']?.split(',') : []

    const filter = []

    filter.push(`${objectXPKField?.name}==${body.objectId}`)

    if (objectXSARSQLFilter) {
      filter.push(objectXSARSQLFilter)
    }

    filter.concat(
      getObjectXSAExtraRsqlFilters({
        config,
        objectXColumnsObjectSAFiltersCriteria,
        objectYSAFiltersCriteria,
      })
    )

    return {
      filter: filter.join(';'),
      expands: getExpandsParamForObjectX(config).concat(objectXParamsExpands),
    }
  }

  const handleCreateItem = (body: MessageBody) => {
    executeCommand({
      objectCode: body.objectCode,
      commandCode: config.objectX.getDataCommand?.name as string,
      method: config.objectX.getDataCommand?.httpMethod,
      ...getObjectXParams(body),
    })
      .unwrap()
      .then(result => {
        const items = result as ObjectDataRecord[]

        if (!items.length) {
          return
        }

        const changedItem = items[0]

        const { itemIndex, key, columnInfo } = getIndicesForChanging(changedItem)

        if (!key || itemIndex === undefined || !isArray(timeLineGridData?.[itemIndex]?.[key])) {
          return
        }

        config.objectX.totalsRows?.forEach((_, rowIndex) => {
          getColumnTotals({
            rowIndex,
            key,
            srcObj: { columnInfo, startDate: columnInfo?.startDate, endDate: columnInfo?.endDate },
          })
        })

        handleAddTimeLineObjectXEvent({
          itemIndex,
          newItem: changedItem,
          key,
          objectId: body.objectId,
        })
      })
  }

  const handleUpdateItem = (body: MessageBody) => {
    if (!objectXPKField?.name) {
      return
    }

    executeCommand({
      objectCode: body.objectCode,
      commandCode: config.objectX.getDataCommand?.name as string,
      method: config.objectX.getDataCommand?.httpMethod,
      ...getObjectXParams(body),
    })
      .unwrap()
      .then(result => {
        const items = result as ObjectDataRecord[]

        if (!items.length) {
          handleDeleteItem(body)

          return
        }

        const changedItem = items[0]

        const { itemIndex, key, columnInfo } = getIndicesForChanging(changedItem)

        const newData = [...timeLineGridData]

        if (!key || itemIndex === undefined) {
          return
        }

        if (!isArray(newData[itemIndex][key])) {
          handleDeleteItem(body)

          return
        }

        const isObjectRecordExist = newData[itemIndex][key]?.find(
          item => item[objectXPKField?.name] === body.objectId
        )

        // Обновление существующей записи
        if (isObjectRecordExist) {
          config.objectX.totalsRows?.forEach((_, rowIndex) => {
            getColumnTotals({
              rowIndex,
              key,
              srcObj: {
                columnInfo,
                startDate: columnInfo?.startDate,
                endDate: columnInfo?.endDate,
              },
            })
          })

          if (dragItemsForGetTotals.current.length) {
            const movesCopy = structuredClone(
              dragItemsForGetTotals.current.map(item => {
                return {
                  ...item,
                  source: {
                    ...item.source,
                    htmlElement: null,
                  },
                  target: {
                    ...item.source,
                    htmlElement: null,
                  },
                }
              })
            )

            movesCopy.forEach(move => {
              if (move.item[objectXPKField?.name] === body.objectId) {
                handleDeleteDragItemForGetTotals(move.sourceColumnId)

                config.objectX.totalsRows?.forEach((_, rowIndex) => {
                  getColumnTotals({
                    rowIndex,
                    key: move.sourceColumnId,
                    srcObj: {
                      columnInfo: move.source?.columnInfo,
                      startDate: move.source?.columnInfo?.startDate,
                      endDate: move.source?.columnInfo?.endDate,
                    },
                  })
                })
              }
            })
          }

          handleUpdateTimeLineObjectXEvent({
            itemIndex,
            key,
            newItem: changedItem,
            objectId: body.objectId,
          })

          return
        }

        // У записи был изменен ресурс или запись была перенесена в другую колонку
        const { columnKeysForGetTotals } = getFilteredItems({
          items: timeLineGridData,
          objectId: body.objectId,
        })

        handleDeleteTimeLineObjectXEventById(body.objectId)
        handleAddTimeLineObjectXEvent({
          itemIndex,
          key,
          newItem: changedItem,
          objectId: body.objectId,
        })

        if (!columnKeysForGetTotals.has(key)) {
          columnKeysForGetTotals.set(key, {
            columnInfo,
            startDate: columnInfo?.startDate,
            endDate: columnInfo?.endDate,
          })
        }

        config.objectX.totalsRows?.forEach((_, rowIndex) => {
          columnKeysForGetTotals.forEach((value, key) => {
            getColumnTotals({ rowIndex, key, srcObj: value })
          })
        })
      })
  }

  const handleDeleteItem = (body: MessageBody) => {
    if (!body.objectId) {
      return
    }

    const { columnKeysForGetTotals } = getFilteredItems({
      items: timeLineGridData,
      objectId: body.objectId,
    })

    config.objectX.totalsRows?.forEach((_, rowIndex) => {
      columnKeysForGetTotals.forEach((value, key) => {
        getColumnTotals({ rowIndex, key, srcObj: value })
      })
    })

    handleDeleteTimeLineObjectXEventById(body.objectId)
  }

  const getFilteredItems = (data: { items: TimeLineGridDataRecord[]; objectId: string }) => {
    const { items, objectId } = data

    const columnKeysForGetTotals = new Map<string, ObjectDataRecord>()

    items.forEach(item => {
      const row = { ...item }

      for (const key in row) {
        if (isArray(row[key as keyof DateIntervalKey])) {
          const elementsForDeleting = row[key as keyof DateIntervalKey]?.filter(
            (item: ObjectDataRecord) => item[objectXPKField?.name as string] === objectId
          )

          elementsForDeleting.forEach((item: ObjectDataRecord) => {
            columnKeysForGetTotals.set(key, {
              columnInfo: item.columnInfo,
              startDate: item?.columnInfo?.startDate,
              endDate: item?.columnInfo?.endDate,
            })
          })
        }
      }
    })

    return {
      columnKeysForGetTotals,
    }
  }

  const [toggleSocket] = useSubscription(
    getObjectCodeUrl(config.objectX.objectCode as string),
    message => {
      const body = parseMessageBody(message.body)

      if (!objectXPKField) {
        return
      }

      switch (body.eventName) {
        case EVENT_NAME_OBJECT_MESSAGE.UPDATE:
          handleUpdateItem(body)
          handleExecuteActionWithSetVariables({ eventCode: EVENT_CODE.ON_SOCKET_CHANGE })

          return
        case EVENT_NAME_OBJECT_MESSAGE.CREATE:
          handleCreateItem(body)
          handleExecuteActionWithSetVariables({ eventCode: EVENT_CODE.ON_SOCKET_CHANGE })

          return
        case EVENT_NAME_OBJECT_MESSAGE.DELETE:
          handleDeleteItem(body)
          handleExecuteActionWithSetVariables({ eventCode: EVENT_CODE.ON_SOCKET_CHANGE })

          return
        default:
          return
      }
    }
  )

  useEffect(() => {
    try {
      const jsonParams = config.jsonConfig ? JSON.parse(config.jsonConfig) : ''

      if (jsonParams?.disableObjectXWebSocketUpdate) {
        toggleSocket(false)
      }
    } catch (error) {
      console.error(error)
    }
  }, [])
}
